import { isBetterAuthApiKey } from '~/lib/johkuApiAuth'

export const SESSION_EXPIRED_STORAGE_KEY = 'johku_session_expired'
export const STAFF_REASON_FLOW_KEY = 'johku_staff_reason_flow'

type StoreLike = {
  state: {
    shopId?: string | null
    user?: unknown
    userData?: { email?: string | null } | null
    baseDomain?: string | null
    sessionNeedsRecovery?: boolean
    sessionExpired?: boolean
  }
  getters?: { apiKey?: string | null }
  dispatch: (action: string, payload?: unknown) => unknown
}

export type SessionRefreshResult =
  | { status: 'ok', token: string }
  | { status: 'expired' }
  | { status: 'transient' }
  | { status: 'staff-reason' }
  | { status: 'switched' }

let suppressRecovery = false
let recovering = false
// Scoped to the shop that actually triggered STAFF_REASON_REQUIRED so a stale
// flag from one shop (or a previous person on a shared/kiosk browser tab)
// can never cause the staff-only "select a reason" screen to appear for a
// different shop, or before we've confirmed anything about the current
// visitor. null = no staff-access flow pending.
let staffReasonFlow: string | null = null
let switchingUser = false
let inFlightRefresh: Promise<SessionRefreshResult> | null = null

export function suppressSessionRecovery (value: boolean) {
  suppressRecovery = value
}

export function isSessionRecoverySuppressed () {
  return suppressRecovery || recovering
}

export function isStaffReasonFlowActive (shopId?: string | null) {
  if (typeof window === 'undefined') {
    return false
  }
  if (staffReasonFlow && (!shopId || staffReasonFlow === shopId)) {
    return true
  }
  try {
    const stored = sessionStorage.getItem(STAFF_REASON_FLOW_KEY)
    if (!stored) {
      return false
    }
    return shopId ? stored === shopId : true
  } catch {
    return false
  }
}

export function markStaffReasonFlow (shopId: string) {
  if (typeof window === 'undefined' || !shopId) {
    return
  }
  staffReasonFlow = shopId
  try {
    sessionStorage.setItem(STAFF_REASON_FLOW_KEY, shopId)
  } catch {
    // sessionStorage can throw in private mode; in-memory flag is enough for this tab.
  }
}

export function clearStaffReasonFlow () {
  staffReasonFlow = null
  if (typeof window === 'undefined') {
    return
  }
  try {
    sessionStorage.removeItem(STAFF_REASON_FLOW_KEY)
  } catch {
    // ignore
  }
}

// Shop token is still valid in better-auth, but PHP only throws
// STAFF_REASON_REQUIRED for allowlisted staff who need a new access reason
// (expired staff TTL). Regular merchants never hit this path.
export function beginStaffReasonAccess (store: StoreLike) {
  if (typeof window === 'undefined' || recovering || !store.state.shopId) {
    return
  }
  markStaffReasonFlow(store.state.shopId)
  if (store.state.sessionNeedsRecovery) {
    store.dispatch('setSessionNeedsRecovery', false)
  }
  if (store.state.sessionExpired) {
    store.dispatch('setSessionExpired', false)
  }
  if (store.state.user) {
    store.dispatch('setUser', null)
  }
}

export function beginSessionRecovery (store: StoreLike) {
  if (typeof window === 'undefined' || suppressRecovery || recovering || isStaffReasonFlowActive(store.state.shopId)) {
    return
  }
  if (!store.state.user) {
    return
  }
  store.dispatch('setSessionNeedsRecovery', true)
}

// The Better Auth cookie is shared by every tab and shop on this domain. Once it
// belongs to someone else, signing out here would sign that user out
// everywhere, so only this shop's user is forgotten before the reload.
export async function reloadForSwitchedSession (store: StoreLike) {
  if (typeof window === 'undefined' || recovering) {
    return
  }
  recovering = true
  suppressRecovery = true
  switchingUser = true
  const shopId = store.state.shopId
  if (shopId) {
    try {
      await fetch('/api/auth/clear-shop-session', {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-johku-shop-id': shopId
        },
        body: JSON.stringify({ shopId })
      })
    } catch {
      // Reload anyway; page load re-checks the shop session.
    }
  }
  window.location.reload()
}

// True when the cookie holds a live session other than the one this tab was
// using, e.g. after signing in again in another tab or shop.
async function cookieHasNewerSession (store: StoreLike): Promise<boolean> {
  const shopId = store.state.shopId
  if (!shopId) {
    return false
  }
  try {
    const current = await fetchCookieSession(shopId)
    if (!current || current === 'transient') {
      return false
    }
    return store.getters?.apiKey !== 'betterauth:' + current.token
  } catch {
    return false
  }
}

export async function recoverExpiredSession (store: StoreLike) {
  if (typeof window === 'undefined' || recovering) {
    return
  }
  if (await cookieHasNewerSession(store)) {
    await reloadForSwitchedSession(store)
    return
  }
  recovering = true
  suppressRecovery = true
  try {
    sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, '1')
  } catch {
    // sessionStorage can throw in private mode; reload still recovers.
  }
  try {
    await store.dispatch('logout')
  } catch {
    // Local sign-out is enough; the reload clears in-memory state.
  }
  window.location.reload()
}

export function reloadApp () {
  if (typeof window === 'undefined') {
    return
  }
  window.location.reload()
}

export async function tryRefreshBetterAuthSession (store: StoreLike): Promise<SessionRefreshResult> {
  if (switchingUser) {
    return { status: 'switched' }
  }
  if (isStaffReasonFlowActive(store.state.shopId)) {
    if (store.state.user) {
      beginStaffReasonAccess(store)
    }
    return { status: 'staff-reason' }
  }
  if (suppressRecovery || recovering || store.state.sessionNeedsRecovery) {
    return { status: 'expired' }
  }
  if (!isBetterAuthApiKey(store.getters?.apiKey)) {
    return { status: 'expired' }
  }
  const shopId = store.state.shopId
  if (!shopId) {
    return { status: 'transient' }
  }
  if (!inFlightRefresh) {
    inFlightRefresh = refreshBetterAuthSession(store, shopId).finally(() => {
      inFlightRefresh = null
    })
  }
  return inFlightRefresh
}

// A 401/403 from the PHP backend is not proof the better-auth session is dead.
// On SSR the cookie was already resolved into the store token for this request;
// on the client, confirm against get-session and this shop before treating
// the user as logged out.
export async function confirmBetterAuthSession (store: StoreLike): Promise<SessionRefreshResult> {
  const apiKey = store.getters?.apiKey
  if (!isBetterAuthApiKey(apiKey)) {
    return { status: 'expired' }
  }
  if (import.meta.server) {
    return { status: 'ok', token: String(apiKey) }
  }
  return tryRefreshBetterAuthSession(store)
}

function isTransientSessionFailure (error: unknown): boolean {
  const status = (error as { status?: number, statusCode?: number, response?: { status?: number } } | null)?.status
    ?? (error as { statusCode?: number })?.statusCode
    ?? (error as { response?: { status?: number } })?.response?.status
  if (status === 408 || status === 429 || status === 0 || (typeof status === 'number' && status >= 500)) {
    return true
  }
  const name = error instanceof Error ? error.name : ''
  const message = error instanceof Error ? error.message : String(error || '')
  return name === 'TimeoutError' || name === 'AbortError' || /failed to fetch|network|timeout|econnreset/i.test(message)
}

function emailsEqual (left?: string | null, right?: string | null) {
  if (!left || !right) {
    return false
  }
  return left.trim().toLowerCase() === right.trim().toLowerCase()
}

// null = no session. Network errors are thrown to the caller.
async function fetchCookieSession (shopId: string): Promise<{ token: string, email: string | null } | null | 'transient'> {
  // Native fetch: better-auth returns HTTP 200 with JSON `null` when there is
  // no session. ofetch/$fetch and the better-auth client can treat that as a
  // transport error (and our keep-alive then ignored it as transient).
  const response = await fetch('/api/auth/get-session', {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'x-johku-shop-id': shopId
    }
  })
  if (response.status === 408 || response.status === 429 || response.status >= 500) {
    return 'transient'
  }
  if (!response.ok) {
    return null
  }
  let data: unknown = null
  try {
    data = await response.json()
  } catch {
    return null
  }
  const { token, email } = parseGetSessionBody(data)
  return token ? { token, email } : null
}

async function refreshBetterAuthSession (store: StoreLike, shopId: string): Promise<SessionRefreshResult> {
  try {
    const current = await fetchCookieSession(shopId)
    if (current === 'transient') {
      return { status: 'transient' }
    }
    if (!current) {
      return { status: 'expired' }
    }
    const knownEmail = store.state.userData?.email
    if (knownEmail && current.email && !emailsEqual(knownEmail, current.email)) {
      // Cookie now belongs to a different global user (other tab / shop login).
      void reloadForSwitchedSession(store)
      return { status: 'switched' }
    }
    const apiKey = 'betterauth:' + current.token
    const shopResult = await confirmShopAcceptsToken(store, shopId, apiKey)
    if (shopResult.status === 'expired') {
      const staffResult = await probeStaffReasonRequired(store, shopId)
      if (staffResult.status === 'staff-reason') {
        beginStaffReasonAccess(store)
        return staffResult
      }
      if (staffResult.status !== 'ok') {
        return staffResult
      }
    } else if (shopResult.status !== 'ok') {
      return shopResult
    }
    store.dispatch('setAuthToken', apiKey)
    return { status: 'ok', token: apiKey }
  } catch (error) {
    if (isTransientSessionFailure(error)) {
      return { status: 'transient' }
    }
    return { status: 'expired' }
  }
}

function isStaffReasonRequiredPayload (payload: unknown): boolean {
  const parsed = unwrapJson(payload)
  if (!parsed || typeof parsed !== 'object') {
    return false
  }
  const data = parsed as Record<string, unknown>
  if (data.code === 'STAFF_REASON_REQUIRED' || data.errorCode === 'STAFF_REASON_REQUIRED') {
    return true
  }
  if (data.data && data.data !== payload) {
    return isStaffReasonRequiredPayload(data.data)
  }
  return false
}

// login.json with pos=true is the only PHP path that returns STAFF_REASON_REQUIRED,
// and only for allowlisted staff without an active staff-access log. Merchants
// either succeed (they still have a POS shop user) or get a generic 403.
async function probeStaffReasonRequired (store: StoreLike, shopId: string): Promise<SessionRefreshResult> {
  try {
    const response = await fetch('/api/auth/backend-sync', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ shopId })
    })
    if (response.status === 408 || response.status === 429 || response.status >= 500) {
      return { status: 'transient' }
    }
    let data: unknown = null
    try {
      data = await response.json()
    } catch {
      data = null
    }
    if (isStaffReasonRequiredPayload(data)) {
      return { status: 'staff-reason' }
    }
    const parsed = unwrapJson(data)
    const user = parsed && typeof parsed === 'object' ? (parsed as { user?: unknown }).user : null
    if (response.ok && user != null && user !== '') {
      store.dispatch('setUser', user)
      return { status: 'ok', token: String(store.getters?.apiKey || '') }
    }
    return { status: 'expired' }
  } catch (error) {
    if (isTransientSessionFailure(error)) {
      return { status: 'transient' }
    }
    return { status: 'expired' }
  }
}

// Native fetch so axios/$fetch interceptors cannot recurse into this confirm.
async function confirmShopAcceptsToken (
  store: StoreLike,
  shopId: string,
  apiKey: string
): Promise<SessionRefreshResult> {
  const baseDomain = store.state.baseDomain
  if (!baseDomain) {
    return { status: 'transient' }
  }
  try {
    const response = await fetch(baseDomain + shopId + '/ping.json', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + apiKey
      }
    })
    if (response.status === 408 || response.status === 429 || response.status >= 500) {
      return { status: 'transient' }
    }
    if (response.status === 401 || response.status === 403) {
      return { status: 'expired' }
    }
    if (!response.ok) {
      return { status: 'transient' }
    }
    return { status: 'ok', token: apiKey }
  } catch (error) {
    if (isTransientSessionFailure(error)) {
      return { status: 'transient' }
    }
    return { status: 'expired' }
  }
}

function unwrapJson (data: unknown): unknown {
  let parsed = data
  // Nitro/h3 can JSON-encode an already-string body, so get-session sometimes
  // arrives as a quoted JSON string instead of an object.
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed)
    } catch {
      return null
    }
  }
  return parsed
}

function parseGetSessionBody (data: unknown): { token: string | null, email: string | null } {
  const parsed = unwrapJson(data)
  if (!parsed || typeof parsed !== 'object') {
    return { token: null, email: null }
  }
  const payload = parsed as {
    session?: { token?: string } | null
    token?: string
    user?: { email?: string } | null
  }
  return {
    token: payload.session?.token || payload.token || null,
    email: payload.user?.email || null
  }
}
